import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from "./client";
import type {
  Company,
  CompanyAlias,
  Org,
  OrgMember,
  OrgMemberRole,
  Portfolio,
} from "@ceo-cmd/shared";

export interface OrgWorkspaceResponse {
  org: Org | null;
  role: OrgMemberRole | null;
  members: Array<{
    userId: string;
    email?: string;
    role: OrgMemberRole;
    status: string;
    createdAt: string;
  }>;
}

export async function getOrgWorkspace(): Promise<OrgWorkspaceResponse> {
  return apiGet<OrgWorkspaceResponse>("/api/org");
}

export async function createOrg(name: string): Promise<Org> {
  const res = await apiPost<{ org: Org }>("/api/org", { name });
  return res.org;
}

export async function inviteOrgMember(
  email: string,
  role: OrgMemberRole = "member",
): Promise<OrgMember> {
  const res = await apiPost<{ member: OrgMember }>("/api/org/members", {
    email,
    role,
  });
  return res.member;
}

export async function updateOrgMemberRole(
  userId: string,
  role: OrgMemberRole,
): Promise<OrgMember> {
  const res = await apiPatch<{ member: OrgMember }>(
    `/api/org/members/${encodeURIComponent(userId)}`,
    { role },
  );
  return res.member;
}

export async function removeOrgMember(userId: string): Promise<void> {
  await apiDelete(`/api/org/members/${encodeURIComponent(userId)}`);
}

export interface OrgPortfolioResponse {
  portfolio: Portfolio;
  companies: Company[];
}

export async function getOrgPortfolio(): Promise<OrgPortfolioResponse> {
  return apiGet<OrgPortfolioResponse>("/api/org/portfolio");
}

export async function createOrgCompany(input: {
  name: string;
  id?: string;
  aliases?: CompanyAlias[];
}): Promise<Company> {
  const res = await apiPost<{ company: Company }>("/api/org/companies", input);
  return res.company;
}

export async function updateOrgCompany(
  companyId: string,
  patch: {
    name?: string;
    status?: "active" | "archived";
    aliases?: CompanyAlias[];
  },
): Promise<Company> {
  const res = await apiPatch<{ company: Company }>(
    `/api/org/companies/${encodeURIComponent(companyId)}`,
    patch,
  );
  return res.company;
}

export async function archiveOrgCompany(companyId: string): Promise<Company> {
  const res = await apiDelete<{ company: Company }>(
    `/api/org/companies/${encodeURIComponent(companyId)}`,
  );
  return res.company;
}

export async function setOrgCompanyAliases(
  companyId: string,
  aliases: CompanyAlias[],
): Promise<Company> {
  const res = await apiPut<{ company: Company }>(
    `/api/org/companies/${encodeURIComponent(companyId)}/aliases`,
    { aliases },
  );
  return res.company;
}
